import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Mismo proyecto que la app móvil. El claim `admin: true` decide quién puede
// llamar a las CFs admin*; sin él, todas devuelven permission-denied.

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export const functions = getFunctions(app, 'europe-west1');

// ── Tipos compartidos con functions/src/admin.ts ────────────────────────────

export interface AdminUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  providerId: string | null;
  disabled: boolean;
  creationTime: string | null;
  lastSignInTime: string | null;
  credits: number | null;
  fullReportsCount: number | null;
  reportsCount: number | null;
  isAdmin: boolean;
}

export interface AdminReport {
  id: string;
  createdAt: number | null;
  source: 'sub' | 'map';
  hasAI: boolean;
}

export interface AdminHostingRelease {
  releaseId: string;
  type: string;
  status: string | null;
  releaseTime: string | null;
  releaseUserEmail: string | null;
  message: string | null;
  versionName: string | null;
  fileCount: number | null;
  versionBytes: number | null;
}

// ── Callables tipadas ──────────────────────────────────────────────────────

export const callListUsers = httpsCallable<
  { pageToken?: string; pageSize?: number },
  { users: AdminUser[]; nextPageToken: string | null }
>(functions, 'adminListUsers');

export const callListHostingReleases = httpsCallable<
  { pageToken?: string; pageSize?: number },
  { releases: AdminHostingRelease[]; nextPageToken: string | null }
>(functions, 'adminListHostingReleases');

export interface AdminAccount {
  email: string | null;
  regIp: string | null;
  regIpAt: number | null; // ms epoch
  regFp: string | null;
  regFpAt: number | null; // ms epoch
  regOrigin: string | null;
  appAttested: boolean;
  suspicious: boolean;
  suspiciousReason: string | null;
  suspiciousAt: number | null; // ms epoch
  freeCreditGranted: boolean;
  marketingConsent: boolean;
  marketingConsentAt: number | null; // ms epoch
  createdAt: number | null; // ms epoch
  lastLoginAt: number | null; // ms epoch
}

export interface AdminSecurityEvent {
  id: string;
  type: string; // source_inspection | report_harvest
  ip: string | null;
  createdAt: number | null; // ms epoch
  meta: Record<string, unknown>;
}

export const callListUserReports = httpsCallable<
  { uid: string },
  {
    credits: number;
    fullReportsCount: number;
    reports: AdminReport[];
    account: AdminAccount;
    securityEvents: AdminSecurityEvent[];
  }
>(functions, 'adminListUserReports');

export interface AdminPurchase {
  id: string;               // transaction/order id
  uid: string;
  email: string | null;
  productId: string | null;
  credits: number | null;
  at: number | null;        // ms epoch
}

// Lista compras (colección `processedPurchases`). Con `uid` → todas las de ese
// usuario (sin paginar). Sin `uid` → lista global paginada por `cursor` (el
// `at` en ms del último doc de la página anterior).
export const callListPurchases = httpsCallable<
  { uid?: string; cursor?: number; pageSize?: number },
  { purchases: AdminPurchase[]; nextCursor: number | null }
>(functions, 'adminListPurchases');

export const callAdjustCredits = httpsCallable<
  { uid: string; delta: number },
  { credits: number }
>(functions, 'adminAdjustCredits');

export const callSetCredits = httpsCallable<
  { uid: string; credits: number },
  { credits: number }
>(functions, 'adminSetCredits');

export const callDeleteReport = httpsCallable<
  { uid: string; reportId: string },
  { success: true; aiCleared: number }
>(functions, 'adminDeleteReport');

export interface AdminReportFull {
  exists: boolean;
  legacyMap: unknown | null;
  report: unknown | null;
  ai: Array<{ id: string; data: unknown }>;
}

export const callGetReport = httpsCallable<
  { uid: string; reportId: string },
  AdminReportFull
>(functions, 'adminGetReport');

export const callClearReportAI = httpsCallable<
  { uid: string; reportId: string },
  { cleared: number }
>(functions, 'adminClearReportAI');

// ── Configuración global de la app ─────────────────────────────────────────
// Documento Firestore `config/app`. Hoy contiene el gate de ForceUpgrade
// (Android). Las claves desconocidas se conservan al mergear: el backoffice
// muestra solo lo que sabe editar.

export interface AppConfig {
  android?: {
    minBuild?: number;
    latestBuild?: number;
    latestVersion?: string;
  };
  // Reservado: ios?: { minBuild; latestBuild; latestVersion }, featureFlags, …
  [key: string]: unknown;
}

export const callGetAppConfig = httpsCallable<
  Record<string, never>,
  { config: AppConfig }
>(functions, 'adminGetAppConfig');

export const callUpdateAppConfig = httpsCallable<
  Partial<AppConfig>,
  { config: AppConfig }
>(functions, 'adminUpdateAppConfig');

// ── Feedback ─────────────────────────────────────────────────────────────────

export type FeedbackStatus = 'new' | 'seen' | 'resolved';

export interface FeedbackContext {
  appVersion: string | null;
  platform: string | null;
  route: string | null;
  userAgent: string | null;
}

export interface AdminFeedback {
  id: string;
  uid: string;
  email: string | null;
  reason: string;
  message: string;
  status: string;
  context: FeedbackContext | null;
  createdAt: number | null; // ms epoch
}

export const callListFeedback = httpsCallable<
  { cursor?: number; pageSize?: number },
  { items: AdminFeedback[]; nextCursor: number | null }
>(functions, 'adminListFeedback');

export const callUpdateFeedbackStatus = httpsCallable<
  { id: string; status: FeedbackStatus },
  { ok: true }
>(functions, 'adminUpdateFeedbackStatus');

export const callDeleteFeedback = httpsCallable<
  { id: string },
  { success: true }
>(functions, 'adminDeleteFeedback');
